import { FtuxConfig, FtuxStep } from '../types';
import { ensureLastStepIsDone } from './utils';

interface PlansConfigOptions {
	hasPlayLesson?: boolean;
	hasDownloadWeek?: boolean;
}

export const getPlansConfig = (options: PlansConfigOptions = {}): FtuxConfig => {
	const { hasPlayLesson = false, hasDownloadWeek = false } = options;

	const steps: FtuxStep[] = [
		{
			type: 'modal' as const,
			headerImage: '/svg/account-created.svg',
			title: 'Welcome to Plans',
			subtitle: 'Let’s look through the different features to get you started!',
			button: {
				label: 'Show me around',
				type: 'full-width' as const
			},
			modalPosition: 'center' as const,
			hasBackdrop: true
		},
		{
			type: 'modal' as const,
			title: 'Weekly Plans',
			subtitle:
				'Each weekly plan belongs to a stage from the LLLL scope and sequence and follows the LLLL teaching model: Revise, Teach, Practise, Apply and Assess.  Each plan contains a series of activities with linked lesson slides or activity resources that will help you conduct your lesson for the day.\n\nPlan out your day by looking at the order of <strong>Teacher Activity Resources</strong>, the duration of each activity, and what printable materials to prepare.',
			image: '/img/ftux-plans-1.png',
			button: {
				label: 'Next',
				type: 'paginated' as const,
				hasBackButton: false
			},
			modalPosition: 'bottom-right' as const,
			hasBackdrop: false
		},
		{
			type: 'modal' as const,
			title: 'Teacher Activity Resources',
			subtitle:
				'<strong>Each day contains 3 types of tiles:</strong>\n\n• <strong>Instructions tiles</strong> tell you what physical activity you should be doing next based on the Teaching Sequence. Refer to the Teacher Activity Resource Books for the relevant stage for additional support.\n\n• <strong>Routines tiles</strong> are digital lessons you use to conduct your lessons and display on your classroom screens for whole-class teaching.\n\n<strong>• Resources tiles</strong> are printable materials to be used in class by your students.',
			image: '/img/ftux-plans-2.png',
			button: {
				label: 'Next',
				type: 'paginated' as const,
				hasBackButton: true
			},
			modalPosition: 'bottom-right' as const,
			hasBackdrop: false
		},
		{
			type: 'tooltip' as const,
			target: '#weekly-plan-navigation',
			subtitle: 'Easily browse through (or view) weekly plans using the arrows here.',
			tooltipPlacement: 'bottom' as const,
			button: {
				label: 'Next',
				type: 'paginated' as const
			},
			hasBackdrop: false
		},
		{
			type: 'tooltip' as const,
			target: '#teaching-sequence-link',
			subtitle: 'Alternatively you can view the Little Learners Teaching Sequence, which lists all the focus graphemes and phonemes for each stage.',
			tooltipPlacement: 'bottom' as const,
			button: {
				label: 'Next',
				type: 'paginated' as const
			},
			hasBackdrop: false
		},
		{
			type: 'tooltip' as const,
			target: '#teaching-sequence-details-link',
			subtitle:
				'Here you can view and select the year level and week you want to view. Each weekly cell will show you the focus for that week.',
			tooltipPlacement: 'left' as const,
			flip: true, // Enable flip: try left first, fallback to top if not enough space
			button: {
				label: 'Next',
				type: 'paginated' as const
			},
			hasBackdrop: false
		},
		{
			type: 'tooltip' as const,
			target: '#weekly-plan-link',
			subtitle: 'Click on a week to quickly navigate to its corresponding plan.',
			tooltipPlacement: 'right' as const,
			button: {
				label: 'Next',
				type: 'paginated' as const
			},
			hasBackdrop: false
		}
	];

	// Conditionally add Play Lesson step only if the element exists
	if (hasPlayLesson) {
		steps.push({
			type: 'tooltip' as const,
			target: '#play-lesson-link',
			subtitle:
				"Click the <strong>Play</strong> button to follow the day’s teaching plan with ready-to-go lesson content.",
			tooltipPlacement: 'right' as const,
			requireTargetActive: true,
			button: {
				label: 'Next',
				type: 'paginated' as const
			},
			hasBackdrop: false
		});
	}

	if (hasDownloadWeek) {
		steps.push({
			type: 'tooltip' as const,
			target: '#download-week-link',
			subtitle: 'Prepare ahead by downloading the full weekly plan and all linked resources using this button.',
			tooltipPlacement: 'bottom' as const,
			requireTargetActive: true,
			button: {
				label: 'Next',
				type: 'paginated' as const
			},
			hasBackdrop: false
		});
	}

	const config: FtuxConfig = {
		id: 'plans-ftux',
		name: 'Plans Tutorial',
		showSkipButton: true,
		steps
	};

	// Ensure the last step always has "Done" as the label
	return ensureLastStepIsDone(config);
};
